import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  IconButton,
  List,
  ListItem,
  Typography,
  Stack,
  Tooltip,
  Box,
  Collapse,
  Grid,
} from '@mui/material';
import { MatchSuggestion } from '@marageti/z4-sdk/lib/travel';
import {
  formatCountryCodesToString, OutlinedIcons, RichTextDisplay, ZAvatar,
} from '@marageti/z4-lib';
import { useMatchingStore } from '../../../store';
import { partnerStatusIcons } from '../../status-icons';
import {
  CardTextWithIcon, Leads, Rating, StandardSales,
} from './match-suggestion-card-components';
import PartnerCapacityDialogContainer from './partner-capacity-dialog-container';

type Props = {
  disabled?: boolean;
  displayCountries?: boolean;
  forCountries?: string[];
  matchSuggestion: MatchSuggestion;
};

const MatchSuggestionCard = ({
  disabled = false,
  displayCountries = false,
  forCountries = [],
  matchSuggestion,
}: Props) => {
  const {
    countryCodes,
    countryMinPPPDs,
    existingMatches,
    onClickMatch,
    selectedCountries,
    selectedMatches,
    tripRequest,
  } = useMatchingStore();
  const itineraryPartnerId = tripRequest?.basedOnItineraryPartnerId;
  const selectedPartnerIds = [...Object.keys(selectedMatches), ...Object.keys(existingMatches)];

  const {
    partner,
    leadMetrics: {
      leadsNow,
      leadsLast3Days,
      leadsLast7Days,
      leadsLast14Days,
      leadsLate,
    },
    adjustedCapacity,
    currentCapacity,
    exclusionReasons,
    standardConversionRate,
    standardSaleCount,
  } = matchSuggestion;

  const selected = !!selectedMatches[partner.id];
  const existingMatch = !!existingMatches[partner.id];
  const primaryCountries = countryCodes ? formatCountryCodesToString(partner.primaryCountries.filter((c) => selectedCountries?.includes(c.country)).map((c) => c.country), countryCodes) : '';
  const secondaryCountries = countryCodes ? formatCountryCodesToString(partner.secondaryCountries.filter((c) => selectedCountries?.includes(c.country)).map((c) => c.country), countryCodes) : '';

  const partnerStatusDisplayMap = {
    ONBOARDING: 'Onboarding',
    NEW: 'New',
    ACTIVE: 'Active',
    NON_STRATEGIC: 'Non Strategic',
    PHASING_OUT: 'Phasing Out',
    INACTIVE: 'Inactive',
  };

  const combinedExclusions = useMemo(() => {
    const e: string[] = [];
    if (selectedPartnerIds) {
      selectedPartnerIds.forEach((pId) => {
        if (partner.noCompete?.[pId]) {
          e.push(`Do not match with ${partner.noCompete[pId]}`);
        }
      });
    }
    return [...exclusionReasons, ...e];
  }, [selectedPartnerIds, partner.noCompete]);

  const minPPPDs = partner.countries.filter((c) => selectedCountries?.includes(c.country))
    .map((c) => `$${Math.max(c.minBudgetPPPD, countryMinPPPDs[c.country] || 0, countryMinPPPDs.Default)} (${countryCodes ? formatCountryCodesToString([c.country], countryCodes) : ''})`)
    .join(', ');

  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const shouldExpand = () => {
      if (disabled && selected) return false;
      if (existingMatches && Object.keys(existingMatches).length > 0) return false;
      if (existingMatch) return false;
      if (combinedExclusions.length === 0) return true;
      return false;
    };

    setExpanded(shouldExpand());
  }, [existingMatches, disabled]);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Card
      variant="loose"
      sx={(theme) => ({
        border: `2px solid ${selected || existingMatch ? theme.palette.primary.main : theme.palette.background.paper}`,
      })}
    >
      <CardHeader
        title={(
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Stack direction="row" spacing={1}>
              <Box sx={{
                display: 'flex',
              }}
              >
                {matchSuggestion.partner.name}
              </Box>
              <Tooltip title={partnerStatusDisplayMap[matchSuggestion.partner.status]} placement="top">
                <Box>
                  {partnerStatusIcons[matchSuggestion.partner.status].icon}
                </Box>
              </Tooltip>
            </Stack>
            {combinedExclusions.length > 0 && (
              <Tooltip
                placement="top"
                sx={{
                  position: 'relative',
                  top: '11px',
                  right: '8px',
                }}
                componentsProps={{ tooltip: { sx: { px: 1.5, maxWidth: '500px' } } }}
                title={(
                  <List sx={{ p: 0, listStylePosition: 'inside' }}>
                    {combinedExclusions.map((r) => (
                      <ListItem
                        key={r}
                        sx={{
                          p: 0, listStyleType: '"- "', display: 'list-item',
                        }}
                      >
                        <Typography variant="captionBold" color="primary.contrastText">
                          {r}
                        </Typography>
                      </ListItem>

                    ))}
                  </List>
              )}
              >
                <Stack direction="row" spacing={0.5}>
                  <OutlinedIcons.OutlinedExclamationCircleIcon color="error" fontSize="medium" />
                </Stack>
              </Tooltip>
            )}
          </Stack>
        )}
        subheader={`${primaryCountries}${secondaryCountries && `, (${secondaryCountries})`}`}
        avatar={(
          <ZAvatar
            size={40}
            src={partner.avatar}
            name={partner.name}
          />
        )}
        titleTypographyProps={{
          variant: 'bodyBold',
        }}
        action={(
          <Stack
            direction="row"
            display="flex"
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              top: '14px',
              right: '8px',
              pl: '4px',
              pr: '0px',
            }}
          >
            <IconButton
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
            >
              {expanded ? (
                <OutlinedIcons.OutlinedChevronDownIcon color="primary" />
              ) : (
                <OutlinedIcons.OutlinedChevronRightIcon color="primary" />
              )}
            </IconButton>
            <Tooltip
              disableHoverListener={(!disabled && !existingMatch) || selected}
              title="Go to trip request matches to cancel this match"
            >
              <IconButton
                onClick={(disabled || existingMatch) ? undefined : (() => onClickMatch(matchSuggestion))}
                sx={(theme) => {
                  const selectedStyles = selected
                    ? {
                      border: `2px solid ${theme.palette.primary.main}`,
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.common.black25,
                        borderColor: theme.palette.common.black25,
                      },
                    }
                    : {
                      border: `2px solid ${theme.palette.common.black50}`,
                    };

                  const disabledAndExistingMatchStyles = (disabled || existingMatch)
                    && {
                      cursor: 'initial',
                      borderColor: theme.palette.common.black25,
                      color: theme.palette.primary.contrastText,
                      backgroundColor: theme.palette.common.black25,
                      '&:hover': {
                        backgroundColor: theme.palette.common.black25,
                      },
                    };

                  return {
                    borderRadius: '50%',
                    height: '25px',
                    width: '25px',
                    ml: '8px',
                    ...selectedStyles,
                    ...disabledAndExistingMatchStyles,
                  };
                }}
              >
                <OutlinedIcons.OutlinedCheckIcon
                  sx={(theme) => ({
                    fontSize: '1.1rem',
                    color: existingMatch || selected ? theme.palette.primary.contrastText : theme.palette.common.black50,
                  })}
                />
              </IconButton>
            </Tooltip>
          </Stack>
          )}
        sx={{
          '& .MuiStack-root': {
            height: '24px',

          },
        }}
      />
      <CardContent sx={{ pb: expanded ? '24px' : '0px !important', pt: expanded ? '24px' : '0px' }}>
        <Collapse in={expanded}>
          {displayCountries && forCountries && (
            <CardContent sx={{ padding: 0 }}>
              <Typography variant="captionItalic" color="primary.main">
                {`Matched for ${countryCodes ? formatCountryCodesToString(forCountries, countryCodes) : '...'}`}
              </Typography>
            </CardContent>
          )}
          <CardContent sx={{ padding: '0px !important' }}>
            <Grid container spacing={{ xs: 1.5, md: 3 }}>
              <Grid item xs={12} md={4.2}>
                <Stack spacing={2}>
                  <CardTextWithIcon
                    icon={<OutlinedIcons.OutlinedInboxInIcon color="disabled" fontSize="medium" />}
                    content={(
                      <PartnerCapacityDialogContainer
                        partnerId={partner.id}
                        currentCapacity={currentCapacity}
                        adjustedCapacity={adjustedCapacity}
                      >
                        {({ handleOpen }) => (
                          <Typography
                            onClick={handleOpen}
                            sx={{
                              cursor: 'pointer',
                              ':hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {`${currentCapacity}/${adjustedCapacity} Capacity`}
                          </Typography>
                        )}
                      </PartnerCapacityDialogContainer>
                      )}
                  />
                  <CardTextWithIcon icon={<OutlinedIcons.OutlinedCurrencyDollarIcon color="disabled" fontSize="medium" />} content={minPPPDs} />
                  <CardTextWithIcon
                    icon={<OutlinedIcons.OutlinedChartSquareBarIcon color="disabled" fontSize="medium" />}
                    content={(
                      <Leads
                        leadsNow={leadsNow}
                        leadsLast3Days={leadsLast3Days}
                        leadsLast7Days={leadsLast7Days}
                        leadsLast14Days={leadsLast14Days}
                        leadsLate={leadsLate}
                      />
                    )}
                  />
                  <CardTextWithIcon
                    icon={<OutlinedIcons.OutlinedChartPieIcon color="disabled" fontSize="medium" />}
                    content={(
                      <StandardSales
                        standardConversionRate={standardConversionRate}
                        standardSaleCount={standardSaleCount}
                      />
                    )}
                  />
                  <CardTextWithIcon
                    icon={<OutlinedIcons.OutlinedStarIcon color="disabled" fontSize="medium" />}
                    content={(
                      <Rating partnerId={partner.id} />
                    )}
                  />
                  {(itineraryPartnerId === partner.id || combinedExclusions.length > 0) && (
                    <Stack direction={{ desktop: 'row' }} spacing={2}>
                      {combinedExclusions.length > 0 && (
                      <Tooltip
                        placement="top-end"
                        componentsProps={{ tooltip: { sx: { px: 1.5, maxWidth: '500px' } } }}
                        title={(
                          <List sx={{ p: 0, listStylePosition: 'inside' }}>
                            {combinedExclusions.map((r) => (
                              <ListItem
                                key={r}
                                sx={{
                                  p: 0, listStyleType: '"- "', display: 'list-item',
                                }}
                              >
                                <Typography variant="captionBold" color="primary.contrastText">
                                  {r}
                                </Typography>
                              </ListItem>
                            ))}
                          </List>
                        )}
                      >
                        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                          <OutlinedIcons.OutlinedExclamationCircleIcon color="error" fontSize="medium" />
                          <Typography variant="caption" color="error" sx={{ pl: '8px', fontSize: '1rem' }}>
                            {`Not Recommended (${combinedExclusions.length} Reason${combinedExclusions.length > 1 ? 's' : ''})`}
                          </Typography>
                        </Stack>
                      </Tooltip>
                      )}
                      {itineraryPartnerId === partner.id && (
                      <Typography variant="captionItalic">
                        Designed incoming itinerary
                      </Typography>
                      )}
                    </Stack>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} md={7.8}>
                <Stack spacing={1.5}>
                  <CardTextWithIcon
                    icon={(
                      <OutlinedIcons.OutlinedPencilAltIcon
                        color="disabled"
                        fontSize="medium"
                      />
                  )}
                    content={(
                      <Box>
                        {partner.notes ? (
                          <RichTextDisplay>
                            {partner.notes}
                          </RichTextDisplay>
                        ) : (
                          <Typography>-</Typography>
                        )}
                      </Box>
                    )}
                  />
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default MatchSuggestionCard;
